import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { DatePicker } from '../DatePicker';
import {
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_LINK_BUTTON_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PAGE_TITLE_CLASSNAME,
  ADMIN_SECTION_TITLE_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_TABLE_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';
import { AdminAccessDenied } from './AdminAccessDenied';
import { StyleNotesViewContent, StyleNotesViewDialog } from './StyleNotesViewDialog';

interface StyleNotesOverview {
  totalRegenerations: number;
  outcomeCounts: Array<{ outcome: string; count: number }>;
  avgCalibrationScoreAtRegeneration: number | null;
  documentsWithStyleNotes: number;
  documentsWithoutStyleNotes: number;
  nearThresholdDocuments: Array<{
    documentId: string;
    documentTitle: string;
    ownerEmail: string;
    styleNotes: string | null;
    calibrationScore: number;
    threshold: number;
    acceptanceRate: number | null;
    stylisticClosesSinceUpdate: number;
    status: string;
  }>;
  config: {
    bootstrapThreshold: number;
    refinementThreshold: number;
    debounceMinutes: number;
  };
}

interface RegenerationLogEntry {
  id: string;
  documentId: string;
  documentTitle?: string | null;
  userEmail?: string | null;
  outcome: string;
  calibrationScore: number;
  threshold: number;
  llmCallId: string | null;
  styleNotesBefore: string | null;
  styleNotesAfter: string | null;
  styleNotesComparison: string | null;
  styleNotesComparisonLlmCallId: string | null;
  createdAt: string;
}

export const StyleNotesAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [overview, setOverview] = useState<StyleNotesOverview | null>(null);
  const [entries, setEntries] = useState<RegenerationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [styleNotesView, setStyleNotesView] = useState<StyleNotesViewContent | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!token && !masterKey) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (masterKey) params.set('masterKey', masterKey);

      const url = `${API_BASE}/api/admin/style-notes/overview?${params.toString()}`;
      const response = await fetch(url, {
        headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to load overview');
      }

      setOverview(await response.json());
    } catch (err) {
      console.error('Error loading style notes overview:', err);
      setError(t('admin_style_notes_overview_load_error'));
    } finally {
      setLoading(false);
    }
  }, [endDate, masterKey, startDate, t, token]);

  const fetchLog = useCallback(async () => {
    if (!token && !masterKey) {
      return;
    }

    setLogLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '25',
      });
      if (selectedOutcome) params.set('outcome', selectedOutcome);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (masterKey) params.set('masterKey', masterKey);

      const url = `${API_BASE}/api/admin/style-notes/regeneration-log?${params.toString()}`;
      const response = await fetch(url, {
        headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to load regeneration log');
      }

      const data = await response.json();
      setEntries(data.entries);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      console.error('Error loading regeneration log:', err);
    } finally {
      setLogLoading(false);
    }
  }, [endDate, masterKey, page, selectedOutcome, startDate, token]);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    void fetchLog();
  }, [fetchLog]);

  if (!user?.admin) {
    return <AdminAccessDenied />;
  }

  if (loading && !overview) {
    return <AdminLoadingState label={t('admin_style_notes_overview_loading')} />;
  }

  const skipCount = overview?.outcomeCounts
    .filter((item) => item.outcome.startsWith('skipped_'))
    .reduce((sum, item) => sum + item.count, 0) ?? 0;

  return (
    <div className={ADMIN_PAGE_CLASSNAME}>
      <h1 className={ADMIN_PAGE_TITLE_CLASSNAME}>
        {t('admin_style_notes_page_title')}
      </h1>

      {error && (
        <p className="mb-4 text-sm text-destructive">{error}</p>
      )}

      <div className={ADMIN_FILTER_BAR_CLASSNAME}>
        <DatePicker value={startDate} onChange={setStartDate} placeholder={t('start_date')} />
        <DatePicker value={endDate} onChange={setEndDate} placeholder={t('end_date')} />
        <select
          value={selectedOutcome}
          onChange={(event) => {
            setSelectedOutcome(event.target.value);
            setPage(1);
          }}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('admin_style_notes_all_outcomes')}</option>
          <option value="regenerated">{t('admin_style_notes_outcome_regenerated')}</option>
          <option value="manual_regenerated">{t('admin_style_notes_outcome_manual_regenerated')}</option>
          <option value="skipped_below_threshold">{t('admin_style_notes_outcome_skipped_below_threshold')}</option>
          <option value="skipped_stability_guard">{t('admin_style_notes_outcome_skipped_stability_guard')}</option>
          <option value="skipped_debounced">{t('admin_style_notes_outcome_skipped_debounced')}</option>
        </select>
      </div>

      {overview && (
        <>
          <div className={ADMIN_STATS_ROW_CLASSNAME}>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('admin_style_notes_stat_regenerations')}</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{overview.totalRegenerations}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('admin_style_notes_stat_skips')}</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{skipCount}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('admin_style_notes_stat_avg_score')}</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>
                {overview.avgCalibrationScoreAtRegeneration?.toFixed(1) ?? '-'}
              </div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('admin_style_notes_stat_with_notes')}</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{overview.documentsWithStyleNotes}</div>
            </div>
            <div className={ADMIN_STAT_CARD_CLASSNAME}>
              <div className={ADMIN_STAT_LABEL_CLASSNAME}>{t('admin_style_notes_stat_without_notes')}</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{overview.documentsWithoutStyleNotes}</div>
            </div>
          </div>

          <p className={`mb-6 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
            {`${t('admin_style_notes_config_bootstrap')}: ${overview.config.bootstrapThreshold} · ${t('admin_style_notes_config_refinement')}: ${overview.config.refinementThreshold} · ${t('admin_style_notes_config_debounce')}: ${overview.config.debounceMinutes} min`}
          </p>

          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            {t('admin_style_notes_near_threshold')}
          </h2>
          <AdminTableWrap>
            <table className={ADMIN_TABLE_CLASSNAME}>
              <thead>
                <tr>
                  <th>{t('document')}</th>
                  <th>{t('user')}</th>
                  <th>{t('admin_style_notes_score_threshold')}</th>
                  <th>{t('admin_style_notes_acceptance_rate')}</th>
                  <th>{t('admin_style_notes_column')}</th>
                  <th>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {overview.nearThresholdDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted-foreground">
                      {t('admin_style_notes_no_near_threshold')}
                    </td>
                  </tr>
                ) : (
                  overview.nearThresholdDocuments.map((doc) => (
                    <tr key={doc.documentId}>
                      <td>{doc.documentTitle}</td>
                      <td>{doc.ownerEmail}</td>
                      <td>{doc.calibrationScore} / {doc.threshold}</td>
                      <td>
                        {doc.acceptanceRate !== null ? `${Math.round(doc.acceptanceRate * 100)}%` : '-'}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setStyleNotesView({
                            title: doc.documentTitle,
                            styleNotes: doc.styleNotes,
                          })}
                          className={ADMIN_INLINE_LINK_BUTTON_CLASSNAME}
                        >
                          {t('admin_style_notes_view_notes')}
                        </button>
                      </td>
                      <td>{t(`admin_style_notes_status_${doc.status}`)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableWrap>
        </>
      )}

      <h2 className={`${ADMIN_SECTION_TITLE_CLASSNAME} mt-6`}>
        {t('admin_style_notes_regeneration_log')}
      </h2>

      {logLoading ? (
        <AdminLoadingState label={t('admin_style_notes_log_loading')} />
      ) : (
        <>
          <AdminTableWrap>
            <table className={ADMIN_TABLE_CLASSNAME}>
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('document')}</th>
                  <th>{t('user')}</th>
                  <th>{t('admin_style_notes_outcome')}</th>
                  <th>{t('admin_style_notes_score_threshold')}</th>
                  <th>{t('admin_style_notes_column')}</th>
                  <th>{t('llm_calls')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{new Date(entry.createdAt).toLocaleString()}</td>
                    <td>{entry.documentTitle || entry.documentId}</td>
                    <td>{entry.userEmail || '-'}</td>
                    <td>{t(`admin_style_notes_outcome_${entry.outcome}`)}</td>
                    <td>{entry.calibrationScore} / {entry.threshold}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setStyleNotesView({
                          title: entry.documentTitle || entry.documentId,
                          styleNotesBefore: entry.styleNotesBefore,
                          styleNotesAfter: entry.styleNotesAfter,
                          regenerationLogEntryId: entry.id,
                          styleNotesComparison: entry.styleNotesComparison,
                          styleNotesComparisonLlmCallId: entry.styleNotesComparisonLlmCallId,
                        })}
                        className={ADMIN_INLINE_LINK_BUTTON_CLASSNAME}
                      >
                        {t('admin_style_notes_view_notes')}
                      </button>
                    </td>
                    <td>
                      {entry.llmCallId ? (
                        <Link to={`/admin/llm-calls?callId=${entry.llmCallId}`} className={ADMIN_TABLE_LINK_CLASSNAME}>
                          {t('admin_style_notes_view_llm_call')}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>

          <AdminPaginationBar
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            alwaysShow
          />
        </>
      )}

      <StyleNotesViewDialog
        content={styleNotesView}
        onClose={() => setStyleNotesView(null)}
        onComparisonUpdated={(logEntryId, comparison, comparisonLlmCallId) => {
          setEntries((current) => current.map((entry) => (
            entry.id === logEntryId
              ? { ...entry, styleNotesComparison: comparison, styleNotesComparisonLlmCallId: comparisonLlmCallId }
              : entry
          )));
          setStyleNotesView((current) => (
            current && current.regenerationLogEntryId === logEntryId
              ? { ...current, styleNotesComparison: comparison, styleNotesComparisonLlmCallId: comparisonLlmCallId }
              : current
          ));
        }}
      />
    </div>
  );
};
